import React, { useContext, useState, useEffect, useCallback } from "react";
import { AuthContext } from "../../shared/context/auth-context";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
	Button,
	//ButtonGroup,
	//ToggleButtonGroup,
	//ToggleButton,
	Form,
	Card,
	Spinner,
	Toast,
	CardGroup,
	OverlayTrigger,
	Tooltip,
	Dropdown,
} from "react-bootstrap";
import { useHttpClient } from "../../shared/hooks/http-hook";
import MultiSceneSelection from "./MultiSceneSelection";
import TicketAssets from "./TicketAssets";
import TicketNotes from "./TicketNotes";
import TicketVideo from "./TicketVideo";
import dateFormat, { masks } from "dateformat";
import ProductionValue from "./ProductionValue";
import useTicketPermissions from "../../shared/hooks/useTicketPermissions";
import TicketAction from "./TicketAction";
import TicketExampleModal from "./TicketExampleModal";
import CollapsibleSection from "./CollapsibleSection";
import CheckoutForm from "./StripeCheckout";

export default function TicketEditor() {
	const authContext = useContext(AuthContext);
	const { ticketId } = useParams();
	const navigate = useNavigate();
	const { sendRequest, isLoading } = useHttpClient();
	const [userOrganizations, setUserOrganizations] = useState([]);
	const [showOrganizationDropdown, setShowOrganizationDropdown] =
		useState(false);
	const [organizationsLoaded, setOrganizationsLoaded] = useState(false);

	// Form fields
	const [loadedAvatars, setLoadedAvatars] = useState([]);
	const [title, setTitle] = useState("");
	const [avatarScenes, setAvatarScenes] = useState([]);
	const [folderId, setFolderId] = useState("");
	const [orgId, setOrgId] = useState("");
	const [script, setScript] = useState("");
	const [note, setNote] = useState(""); // single note for new ticket
	const [notes, setNotes] = useState([]); // array of notes for existing ticket
	const [assetLink, setAssetLink] = useState(""); // For TicketAssets component
	const [dueDate, setDueDate] = useState("");
	const [displayDueDate, setDisplayDueDate] = useState("");
	const [createdAt, setCreatedAt] = useState("");
	const [status, setStatus] = useState(""); // e.g. "open", "closed", etc.
	const [productionType, setProductionType] = useState("");
	const [videoId, setVideoId] = useState("");
	const [watermarkVideoId, setWatermarkVideoId] = useState("");
	const [assigneeId, setAssigneeId] = useState("");
	const [deposit, setDeposit] = useState(false);
	const [additionalFee, setAdditionalFee] = useState(null);
	const [waivePayment, setWaivePayment] = useState(false);
	const [paid, setPaid] = useState(false);
	const [finalIteration, setFinalIteration] = useState(false);
	const [waived, setWaived] = useState(false);
	//const [expectedCredits, setExpectedCredits] = useState(0);
	//const [showExpectedCredits, setShowExpectedCredits] = useState(false);
	const [scripts, setScripts] = useState([]); // For script selection if needed
	const [selectedScript, setSelectedScript] = useState(null);

	//Comparison fields for ticket update
	const [originalTitle, setOriginalTitle] = useState("");
	const [originalScript, setOriginalScript] = useState("");
	const [originalAvatarScenes, setOriginalAvatarScenes] = useState([]);
	const [originalFolderId, setOriginalFolderId] = useState("");
	const [originalDueDate, setOriginalDueDate] = useState("");
	const [originalProductionType, setOriginalProductionType] = useState("");

	// Additional states
	const [loadingTicket, setLoadingTicket] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");
	const [showToast, setShowToast] = useState(false);

	const [showExampleModal, setShowExampleModal] = useState(false);
	const [exampleType, setExampleType] = useState(null);

	const [isEditMode, setIsEditMode] = useState(false);

	masks.displayDate = "mmm d, h:mm tt";
	masks.dueDate = "mmm d";

	//multistep
	const [currentStep, setCurrentStep] = useState(0);
	const steps = [
		"title",
		"avatars",
		"script",
		"assets",
		"notes",
		"productionType",
	];

	function handleSetProductionType(type) {
		setProductionType(type);
		/*let expectedCredits = 0;
		if (type === "standard") expectedCredits = 60;
		if (type === "pro") expectedCredits = 120;
		if (type === "cinematic") expectedCredits = 420;
		setExpectedCredits(expectedCredits);
		setShowExpectedCredits(true);*/
	}

	const shouldDisableNext = (stepIndex) => {
		switch (steps[stepIndex]) {
			case "title":
				return !title.trim();
			case "avatars":
				return avatarScenes.length === 0;
			case "script":
				return !script.trim();
			case "notes":
				return !note.trim();
			case "productionType":
				return !productionType;
			default:
				return false;
		}
	};

	const fetchScripts = useCallback(async () => {
		try {
			const response = await sendRequest("/api/scripts");
			const responseData = response.data;
			if (Array.isArray(responseData)) {
				setScripts(responseData);
			} else {
				console.error("Response data is not an array:", responseData);
				setScripts([]);
			}
		} catch (err) {
			console.error("Error fetching scripts:", err);
		}
	}, [sendRequest]);

	const renderStep = (step) => {
		switch (step) {
			case "title":
				return (
					<>
						<h4>First, let's add a title</h4>
						<Form.Group className="mb-3" controlId="ticketTitle">
							<Form.Label>Title</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter video title"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								required
							/>
						</Form.Group>
					</>
				);
			case "avatars":
				return (
					<>
						<h4>Let's add a ratava, or multiple, for your video</h4>
						<MultiSceneSelection
							loadedScenes={avatarScenes}
							onChange={(scenes) => setAvatarScenes(scenes)}
							loadedAvatars={loadedAvatars}
							locked={!canEditScenes}
							producerView={userPermissions.hasProducerPrivileges}
							adminView={userPermissions.hasAdminPrivileges}
						/>
					</>
				);
			case "script":
				return (
					<>
						<h4>Let's add a script</h4>
						<p>
							Please enter the script for your video. This will help us create a
							more accurate video.
						</p>

						<Form.Group className="mb-3" controlId="ticketScript">
							<Form.Label>Script</Form.Label>
							<Form.Control
								as="textarea"
								rows={3}
								placeholder="Please enter your script"
								value={script}
								onChange={(e) => setScript(e.target.value)}
								required
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="scriptSelect">
							<Form.Label>Or select a script</Form.Label>
							<Form.Control
								as="select"
								value={selectedScript || ""}
								onChange={(e) => {
									const scriptId = e.target.value;
									setSelectedScript(scriptId);

									const selected = scripts.find((s) => s.id === scriptId);
									if (selected) setScript(selected.content);
								}}
							>
								<option value="">Choose from existing scripts</option>
								{scripts.map((s) => (
									<option key={s.id} value={s.id}>
										{s.title}
									</option>
								))}
							</Form.Control>
						</Form.Group>
					</>
				);
			case "assets":
				return (
					<>
						<h4>(Optional) Let's add some assets</h4>
						<p>
							You can add assets like images, videos, or documents that are
							related to this video. If you don't require any assets, you can
							skip this step.
						</p>
						<p>
							You can also add assets with a simple public Google Drive link
							below.
						</p>
						<Form.Group className="mb-3" controlId="ticketTitle">
							<Form.Label>Drive Link</Form.Label>
							<Form.Control
								type="text"
								placeholder="Public google drive link"
								value={assetLink}
								onChange={(e) => setAssetLink(e.target.value)}
								required
							/>
						</Form.Group>
						<TicketAssets
							title={title}
							folderId={folderId}
							orgId={orgId}
							onFolderSelect={(newFolderId) => setFolderId(newFolderId)}
							locked={!canEditAssets}
							ticketId={ticketId}
						/>
					</>
				);
			case "notes":
				return (
					<>
						<h4>Let's add some notes, the more the better!</h4>
						<p>
							Please give us any information you would like us know ex. desired
							length of video, style / tone, where it will be used, etc.
						</p>
						<Form.Group controlId="ticketNotes">
							<Form.Label>Notes</Form.Label>
							<Form.Control
								as="textarea"
								rows={3}
								placeholder="I want the video to be 30 seconds long, with a professional tone. It will be used for our website."
								value={note}
								onChange={(e) => setNote(e.target.value)}
							/>
						</Form.Group>
					</>
				);
			case "productionType":
				const productionOptions = {
					standard: {
						label: "Standard",
						videoUrl:
							"https://drive.google.com/file/d/19Dx6-O61SiKtKfFVO5xx1-9zePo3audN/preview",
						description: (
							<>
								<p>
									This is our base production level. With “standard” production,
									you get a high-quality video with minimal editing. This is
									recommended for simple messages, social posts, and captions.
								</p>
								<p>
									<b>1-2 Days</b>
								</p>
								<p>
									<b>Price: $100</b>
								</p>
							</>
						),
					},
					pro: {
						label: "Pro",
						videoUrl:
							"https://drive.google.com/file/d/1D_azhGxsW6_hmCl81uN814kg-R_9BlAO/preview",
						description: (
							<>
								<p>
									Our editors ramp it up with more cuts, transitions, music,
									branding, captions, and title cards. Ideal for pitch videos
									and marketing content.
								</p>
								<p>
									<b>2-4 Days</b>
								</p>
								<p>
									<b>Price: $200</b>
								</p>
							</>
						),
					},
					cinematic: {
						label: "Cinematic",
						videoUrl:
							"https://drive.google.com/file/d/13j1L0MZStAKf97ek6yXJEaUMW3D9eXVg/preview",
						description: (
							<>
								<p>
									Our highest production level. Full service: scripting, visual
									effects, post-production polish. Best for viral campaigns and
									high-end marketing.
								</p>
								<p>
									<b>4+ Days</b>
								</p>
								<p>
									<b>Price: $500</b>
								</p>
							</>
						),
					},
				};
				return (
					<>
						<h4>Select a Production Type</h4>
						<Form.Group className="mb-3" controlId="ticketProductionType">
							<Form.Label>Production Type</Form.Label>
							<div className="d-flex gap-3">
								{Object.entries(productionOptions).map(([key, option]) => (
									<div
										key={key}
										className={`flex-grow-1 border p-3 rounded d-flex flex-column align-items-start ${
											productionType === key
												? "border-primary"
												: "border-gray-200"
										}`}
										role="button"
										onClick={() => handleSetProductionType(key)}
										style={{ cursor: "pointer", width: "100%" }}
									>
										{/* Label and Radio */}
										<div className="d-flex w-100 justify-content-between align-items-center mb-2">
											<Form.Check
												type="radio"
												label={<span className="fw-bold">{option.label}</span>}
												name="productionType"
												value={key}
												checked={productionType === key}
												onChange={(e) =>
													handleSetProductionType(e.target.value)
												}
											/>
										</div>

										{/* Video */}
										<div
											style={{
												position: "relative",
												width: "100%",
												paddingBottom: "56.25%",
												marginBottom: "1rem",
											}}
										>
											<iframe
												title={`${option.label} Example`}
												src={option.videoUrl}
												allow="encrypted-media"
												allowFullScreen
												style={{
													position: "absolute",
													top: 0,
													left: 0,
													border: "none",
													width: "100%",
													height: "100%",
												}}
											/>
										</div>

										{/* Description */}
										<div>{option.description}</div>
									</div>
								))}
							</div>
						</Form.Group>
						{/*showExpectedCredits && (
							<p className="text-sm text-gray-500 mr-4">
								Expected Credits: {expectedCredits}
							</p>
						)*/}
					</>
				);
			case "review":
				return (
					<div>
						<h4>Review & Submit</h4>
						<p>Title: {title}</p>
						<p>Scenes: {avatarScenes.length}</p>
						{/*<p className="text-sm text-gray-500 mr-4">
							Expected Credits: {expectedCredits}
						</p>*/}
					</div>
				);
			default:
				return null;
		}
	};

	const isAssignedProducer = authContext.userId === assigneeId; /* &&
		(status === "assigned" || status === "in progress");
	const producerView = authContext.isProducer && isAssignedProducer;
	const adminView = authContext.isSuperAdmin && !isAssignedProducer; */ //fix this so adminview isn't true. Use role from useTicketPermissions
	const userRoles = {
		isSuperAdmin: authContext.isSuperAdmin,
		isProducer: authContext.isProducer,
		isAssignedProducer: isAssignedProducer,
	};

	const userPermissions = {
		hasAdminPrivileges: userRoles.isSuperAdmin,
		hasProducerPrivileges:
			userRoles.isAssignedProducer || userRoles.isSuperAdmin,
		hasUserPrivileges: !userRoles.isSuperAdmin && !userRoles.isAssignedProducer,
	};

	const producerView =
		userPermissions.hasProducerPrivileges && !userPermissions.hasAdminPrivileges;
	const adminView = userPermissions.hasAdminPrivileges;

	const {
		canEditTitle,
		canEditProductionType,
		canEditScript,
		canEditScenes,
		canEditAssets,
		canEditDueDate,
	} = useTicketPermissions({
		producerView,
		adminView,
		status,
	});

	const fetchTicket = useCallback(async () => {
		try {
			setLoadingTicket(true);
			const response = await sendRequest(`/api/tickets/${ticketId}`, "GET");
			// Suppose response.data has { title, avatarIds, folderId, orgId, notes, dueDate, etc. }
			const t = response.data;
			setTitle(t.title || "");
			setAvatarScenes(t.avatarIds || []); // e.g. "avatar1, avatar2"
			setFolderId(t.folderId || "");
			setOrgId(t.organizationId || "");
			setScript(t.script || ""); // script for the video
			setNotes(t.notes || []); // if notes is an array, we just show them as multi-line
			setAssetLink(t.assetLink || ""); // optional asset link
			if (t.dueDate && t.dueDate._seconds) {
				const dateObj = new Date(t.dueDate._seconds * 1000);
				setDueDate(dateObj.toISOString().split("T")[0]); // format as YYYY-MM-DD
				setOriginalDueDate(dateObj.toISOString().split("T")[0]);
				setDisplayDueDate(dateFormat(dateObj, "dueDate"));
			} else {
				setDueDate("");
				setDisplayDueDate("");
				setOriginalDueDate("");
			}
			setStatus(t.status || ""); // e.g. "open", "closed"
			setProductionType(t.productionType || "");
			setShowOrganizationDropdown(false);
			setVideoId(t.videoId || "");
			setWatermarkVideoId(t.watermarkVideoId || "");
			setCreatedAt(
				t.createdAt
					? dateFormat(new Date(t.createdAt._seconds * 1000), "displayDate")
					: ""
			);
			setAssigneeId(t.assigneeId || "");
			setDeposit(t.deposit || false);
			setFinalIteration(t.finalIteration || false);
			setWaived(t.waived || false);
			setPaid(t.paid || false);
			// Set original values for comparison
			setOriginalTitle(t.title || "");
			setOriginalScript(t.script || "");
			setOriginalAvatarScenes(t.avatarIds || []);
			setOriginalFolderId(t.folderId || "");
			setOriginalProductionType(t.productionType || "");
		} catch (err) {
			console.error("Error loading ticket:", err);
			setErrorMsg(err.message || "Failed to load ticket");
		} finally {
			setLoadingTicket(false);
		}
	}, [ticketId, sendRequest]);

	const ticketActionProps = {
		ticketId,
		status,
		permissions: userPermissions,
		onActionComplete: fetchTicket,
		assigneeId,
		videoId,
		setVideoId,
		watermarkVideoId,
		setWatermarkVideoId,
		deposit,
		additionalFee,
		setAdditionalFee,
		waivePayment,
		setWaivePayment,
		finalIterationFlag: finalIteration,
		waived,
	};

	// 1. If we have a ticketId, fetch the existing ticket
	useEffect(() => {
		//load avatars
		const fetchAvatars = async () => {
			try {
				const response = await sendRequest("/api/avatars", "GET");
				setLoadedAvatars(response.data);
			} catch (err) {
				console.error("Error loading avatars:", err);
			}
		};

		const fetchUserOrganizations = async () => {
			try {
				let organizations = [];

				if (userPermissions.hasAdminPrivileges) {
					const response = await sendRequest(
						"/api/organizations",
						"GET",
						null,
						{
							Authorization: "Bearer " + authContext.token,
						}
					);
					organizations = response.data;
				} else {
					organizations = authContext.memberships;
				}

				setUserOrganizations(organizations);

				const showDropdown =
					userPermissions.hasAdminPrivileges || organizations.length > 1;
				setShowOrganizationDropdown(showDropdown);
				setOrganizationsLoaded(true);
			} catch (err) {
				console.error("Error fetching organizations:", err);
			}
		};

		fetchAvatars();

		if (ticketId) {
			setIsEditMode(true);
			fetchTicket();
		} else {
			// Creating new ticket
			fetchScripts();
			fetchUserOrganizations();
			setStatus("new");
			setIsEditMode(false);
		}
	}, [
		ticketId,
		sendRequest,
		authContext.token,
		userPermissions.hasAdminPrivileges,
		authContext.memberships,
		fetchTicket,
		fetchScripts,
	]);

	//useeffect to changed locked based on view

	useEffect(() => {
		// if user organizations are loaded and creating a new ticket, set default orgId
		if (organizationsLoaded) {
			if (showOrganizationDropdown) {
				setOrgId(userOrganizations[0]?.orgId || ""); // Set default to first organization
			} else if (userOrganizations.length === 1) {
				setOrgId(userOrganizations[0].orgId); // Set default to single organization
			} else {
				console.error("No organization found for user");
				setErrorMsg("No organization found for user");
			}
		}
	}, [organizationsLoaded, showOrganizationDropdown, userOrganizations]);

	// 2. Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrorMsg("");

		const body = {};
		if (canEditTitle && originalTitle !== title) body.title = title;
		if (canEditScenes && originalAvatarScenes !== avatarScenes)
			body.avatarIds = avatarScenes;
		if (canEditScript && originalScript !== script) body.script = script;
		if (canEditAssets && originalFolderId !== folderId)
			body.folderId = folderId;
		if (canEditDueDate && originalDueDate !== dueDate) {
			body.dueDate = new Date(dueDate);
		}
		if (canEditProductionType && originalProductionType !== productionType)
			body.productionType = productionType;

		try {
			if (isEditMode) {
				await sendRequest(
					`/api/tickets/${ticketId}`,
					"PATCH",
					JSON.stringify(body),
					{
						"Content-Type": "application/json",
					}
				);
				fetchTicket();
			} else {
				// Validate form fields
				if (!avatarScenes.length) {
					setErrorMsg("Please select at least one avatar scene");
					setShowToast(true);
					return;
				} else if (!productionType) {
					setErrorMsg("Please select a production type");
					setShowToast(true);
					return;
				}

				body.orgId = orgId;
				body.note = note;

				const response = await sendRequest(
					"/api/tickets",
					"POST",
					JSON.stringify(body),
					{
						"Content-Type": "application/json",
					}
				);
				navigate(`/tickets/${response.data.id}`);
			}
		} catch (err) {
			console.error("Error saving ticket:", err);
			setErrorMsg(err.message || "Failed to save ticket");
			setShowToast(true);
		}
	};

	//const handleViewChange = (val) => setStatus(val);

	const hasChanged = (
		title,
		avatarScenes,
		folderId,
		dueDate,
		productionType,
		script
	) => {
		return (
			title !== originalTitle ||
			avatarScenes !== originalAvatarScenes ||
			folderId !== originalFolderId ||
			dueDate !== originalDueDate ||
			productionType !== originalProductionType ||
			script !== originalScript
		);
	};

	function handleShowExampleModal(type) {
		// Function to handle showing the example modal
		console.log("Showing example modal for type:", type);
		setExampleType(type);
		setShowExampleModal(true);
	}

	function handleCloseExampleModal() {
		// Function to handle closing the example modal
		console.log("Closing example modal");
		setExampleType(null);
		setShowExampleModal(false);
	}

	const handleDeleteTicket = async (ticketId) => {
		if (!ticketId) return;
		try {
			await sendRequest(`/api/tickets/${ticketId}`, "DELETE", null, {
				Authorization: "Bearer " + authContext.token,
			});
			navigate("/tickets");
		} catch (err) {
			console.error("Error deleting ticket:", err);
			setErrorMsg(err.message || "Failed to delete ticket");
			setShowToast(true);
		}
	};

	return (
		<div>
			<div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999 }}>
				<Toast
					show={showToast}
					onClose={() => setShowToast(false)}
					delay={5000}
					autohide
				>
					<Toast.Body>{errorMsg}</Toast.Body>
				</Toast>
			</div>
			<div className="bg-white p-4">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center space-x-4 mb-4">
						{/* Back button */}
						<button
							className="p-2 hover:bg-gray-100 rounded-full transition-colors"
							onClick={() => navigate("/tickets")}
						>
							<ArrowLeft className="w-6 h-6" />
						</button>
						<span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
							{status &&
								status
									.split(" ")
									.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
									.join(" ")}
						</span>
						{finalIteration && status === "in review" && (
							<span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-orange-100 text-orange-800">
								Final Iteration
							</span>
						)}
						{waived && (
							<span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
								Payment Waived
							</span>
						)}
						{(userPermissions.hasAdminPrivileges || userPermissions.hasProducerPrivileges) && (
							<>
								{ticketId && (
									<p className="text-sm text-gray-500">
										ID: {ticketId} {/* Display ticket ID if available */}
									</p>
								)}
								{!deposit && status === "assigned" && (
									<span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-gray-800">
										Deposit has not been paid, do not start work until payment
										is made
									</span>
								)}
							</>
						)}
						{deposit && (
							<span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-gray-800">
								{paid ? "Payment Complete" : "Deposit Paid"}
							</span>
						)}
					</div>
					{userPermissions.hasAdminPrivileges && (
						<div className="relative">
							<Dropdown>
								<Dropdown.Toggle
									variant="light"
									className="p-1 border rounded-circle"
									id="ticket-settings-dropdown"
								>
									<span className="text-xl">⋮</span>
								</Dropdown.Toggle>
								<Dropdown.Menu align="end">
									<Dropdown.Item
										onClick={() => {
											if (
												window.confirm(
													"Are you sure you want to delete this ticket?"
												)
											) {
												handleDeleteTicket(ticketId);
											}
										}}
										className="text-danger"
									>
										Delete Ticket
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</div>
					)}
				</div>

				{loadingTicket ? (
					<div className="flex items-center justify-center mt-20">
						<Spinner animation="border" />
					</div>
				) : (
					<>
						{isEditMode ? (
							<>
								{/*section to start logic - is the ticket ASSIGNED, display deposit page instead of normal view for USER only */}
								{status === "assigned" &&
								userPermissions.hasUserPrivileges ? (
									<>
										<Card className="p-4">
											<Card.Body>
												<h3 className="mb-3">Deposit Required</h3>
												<p>
													To proceed with this ticket, a deposit is required.
													This deposit will be applied to the final cost of the
													video.
												</p>
												<p>
													Please complete the payment to continue with the
													ticket processing.
												</p>
												<CheckoutForm ticketId={ticketId} />
											</Card.Body>
										</Card>
									</>
								) : status === "complete" &&
								  !paid &&
								  !userPermissions.hasAdminPrivileges &&
								  !userPermissions.hasProducerPrivileges ? (
									<>
										<Card className="p-4">
											<Card.Body>
												<h3 className="mb-3">Payment Required</h3>
												<p>
													Your video is ready! To view it, please complete the
													payment.
												</p>
												<CheckoutForm ticketId={ticketId} />
											</Card.Body>
										</Card>
									</>
								) : (
									<Card className="p-4">
										<Card.Body>
											<Form onSubmit={handleSubmit}>
												{canEditTitle ? (
													<Form.Group className="mb-3" controlId="ticketTitle">
														<Form.Label>Title</Form.Label>
														<Form.Control
															type="text"
															placeholder="Enter ticket title"
															value={title}
															onChange={(e) => setTitle(e.target.value)}
															required
														/>
													</Form.Group>
												) : (
													<h2>{title}</h2>
												)}

												{paid && status === "complete" ? (
													<TicketVideo driveId={videoId} />
												) : (
													<>
														{deposit && watermarkVideoId && (
															<TicketVideo driveId={watermarkVideoId} />
														)}
													</>
												)}

												{/* info cardgroup */}

												<CardGroup className="mb-3">
													<Card>
														<Card.Body>
															<Card.Title>Expected Completion Date</Card.Title>
															<Card.Text>{displayDueDate}</Card.Text>
														</Card.Body>
													</Card>
													<Card>
														<Card.Body>
															<Card.Title>Created At</Card.Title>
															<Card.Text>{createdAt}</Card.Text>
														</Card.Body>
													</Card>
													<Card>
														<Card.Body>
															<Card.Title>Production Value</Card.Title>
															<Card.Text>
																{productionType && (
																	<ProductionValue value={productionType} />
																)}
															</Card.Text>
														</Card.Body>
													</Card>
												</CardGroup>

												<MultiSceneSelection
													loadedScenes={avatarScenes}
													onChange={(scenes) => setAvatarScenes(scenes)}
													loadedAvatars={loadedAvatars}
													locked={!canEditScenes}
													producerView={userPermissions.hasProducerPrivileges}
													adminView={userPermissions.hasAdminPrivileges}
												/>
												<CollapsibleSection title="Assets">
													{assetLink && (
														<p>
															Drive Link:{" "}
															<a
																href={assetLink}
																target="_blank"
																rel="noopener noreferrer"
															>
																{assetLink}
															</a>
														</p>
													)}
													<TicketAssets
														title={title}
														folderId={folderId}
														orgId={orgId}
														onFolderSelect={(newFolderId) =>
															setFolderId(newFolderId)
														}
														locked={!canEditAssets}
														ticketId={ticketId}
													/>
												</CollapsibleSection>
												{showOrganizationDropdown && (
													<CollapsibleSection title="Organization">
														<Form.Group
															className="mb-3"
															controlId="ticketOrgId"
														>
															<Form.Label>Organization</Form.Label>
															<Form.Control
																as="select"
																value={orgId}
																onChange={(e) => setOrgId(e.target.value)}
																required
															>
																<option value="">Select an organization</option>
																{userOrganizations.map((org) => (
																	<option key={org.orgId} value={org.orgId}>
																		{org.name}
																	</option>
																))}
															</Form.Control>
															<Form.Text className="text-muted">
																If not provided, it will use user's default org
																if only one membership
															</Form.Text>
														</Form.Group>
													</CollapsibleSection>
												)}

												<CollapsibleSection title="Messages">
													<TicketNotes
														ticketId={ticketId}
														notes={notes}
														authContext={authContext}
													/>
												</CollapsibleSection>

												<CollapsibleSection title="Script">
													{canEditScript ? (
														<Form.Group
															className="mb-3"
															controlId="ticketScript"
														>
															<Form.Label>Script</Form.Label>
															<Form.Control
																as="textarea"
																rows={3}
																placeholder="Please enter your script"
																value={script}
																onChange={(e) => setScript(e.target.value)}
																required
															/>
														</Form.Group>
													) : (
														<p>
															<strong>Script:</strong>{" "}
															{script || "No script provided"}
														</p>
													)}
												</CollapsibleSection>
												{canEditDueDate && (
													<CollapsibleSection title="Due Date">
														<Form.Group
															className="mb-3"
															controlId="ticketDueDate"
														>
															<Form.Label>Due Date</Form.Label>
															<Form.Control
																type="date"
																value={dueDate}
																onChange={(e) => setDueDate(e.target.value)}
															/>
														</Form.Group>
													</CollapsibleSection>
												)}

												{canEditProductionType && (
													<CollapsibleSection title="Production Type">
														<Form.Group
															className="mb-3"
															controlId="ticketProductionType"
														>
															<Form.Label>Production Type</Form.Label>
															<div className="d-flex flex-column gap-2">
																<div
																	className={`border p-3 rounded d-flex align-items-center justify-content-between ${
																		productionType === "standard"
																			? "border-primary"
																			: "border-gray-200"
																	}`}
																	role="button"
																	onClick={() =>
																		handleSetProductionType("standard")
																	}
																	style={{ cursor: "pointer" }}
																>
																	<Form.Check
																		type="radio"
																		label={<span>Standard</span>}
																		name="productionType"
																		value="standard"
																		checked={productionType === "standard"}
																		onChange={(e) =>
																			handleSetProductionType(e.target.value)
																		}
																	/>
																	<OverlayTrigger
																		placement="left"
																		overlay={
																			<Tooltip id="tooltip-left">
																				Click here for an example
																			</Tooltip>
																		}
																	>
																		<button
																			zIndex="1000" // Ensure the button appears above other elements
																			type="button"
																			className="btn btn-sm btn-outline-secondary" // Use btn-sm for small button
																			style={{ padding: "2px 8px" }} // Adjust padding if needed
																			onClick={(e) => {
																				e.stopPropagation();
																				handleShowExampleModal("standard");
																			}}
																		>
																			?
																		</button>
																	</OverlayTrigger>
																</div>
																<div
																	className={`border p-3 rounded d-flex align-items-center justify-content-between ${
																		productionType === "pro"
																			? "border-primary"
																			: "border-gray-200"
																	}`}
																	role="button"
																	onClick={() => handleSetProductionType("pro")}
																	style={{ cursor: "pointer" }}
																>
																	<Form.Check
																		type="radio"
																		label={<span>Pro</span>}
																		name="productionType"
																		value="pro"
																		checked={productionType === "pro"}
																		onChange={(e) =>
																			handleSetProductionType(e.target.value)
																		}
																	/>
																	<OverlayTrigger
																		placement="left"
																		overlay={
																			<Tooltip id="tooltip-left">
																				Click here for an example
																			</Tooltip>
																		}
																	>
																		<button
																			zIndex="1000" // Ensure the button appears above other elements
																			type="button"
																			className="btn btn-sm btn-outline-secondary" // Use btn-sm for small button
																			style={{ padding: "2px 8px" }} // Adjust padding if needed
																			onClick={(e) => {
																				e.stopPropagation();
																				handleShowExampleModal("pro");
																			}}
																		>
																			?
																		</button>
																	</OverlayTrigger>
																</div>
																<div
																	className={`border p-3 rounded d-flex align-items-center justify-content-between ${
																		productionType === "cinematic"
																			? "border-primary"
																			: "border-gray-200"
																	}`}
																	role="button"
																	onClick={() =>
																		handleSetProductionType("cinematic")
																	}
																	style={{ cursor: "pointer" }}
																>
																	<Form.Check
																		type="radio"
																		label={<span>Cinematic</span>}
																		name="productionType"
																		value="cinematic"
																		checked={productionType === "cinematic"}
																		onChange={(e) =>
																			handleSetProductionType(e.target.value)
																		}
																	/>
																	<OverlayTrigger
																		placement="left"
																		overlay={
																			<Tooltip id="tooltip-left">
																				Click here for an example
																			</Tooltip>
																		}
																	>
																		<button
																			zIndex="1000" // Ensure the button appears above other elements
																			type="button"
																			className="btn btn-sm btn-outline-secondary" // Use btn-sm for small button
																			style={{ padding: "2px 8px" }} // Adjust padding if needed
																			onClick={(e) => {
																				e.stopPropagation();
																				handleShowExampleModal("cinematic");
																			}}
																		>
																			?
																		</button>
																	</OverlayTrigger>
																</div>
															</div>
														</Form.Group>
													</CollapsibleSection>
												)}

												{/* BUTTON TO CHANGE BASED ON STATUS AND USER TYPE */}
												{ticketId ? (
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
														}}
													>
														{status !== "complete" && (
															<div>
																<Button
																	variant="primary"
																	type="submit"
																	disabled={
																		isLoading ||
																		!hasChanged(
																			title,
																			avatarScenes,
																			folderId,
																			dueDate,
																			productionType,
																			script
																		)
																	}
																	className="mr-2"
																>
																	{isLoading
																		? "Saving..."
																		: isEditMode
																		? "Update Ticket"
																		: "Create Ticket"}
																</Button>
															</div>
														)}
													</div>
												) : (
													<div className="mt-4 flex justify-end">
														{/*showExpectedCredits && (
													<p className="text-sm text-gray-500 mr-4">
														Expected Credits: {expectedCredits}
													</p>
												)*/}
														<Button
															variant="primary"
															type="submit"
															disabled={
																isLoading ||
																!hasChanged(
																	title,
																	avatarScenes,
																	folderId,
																	dueDate,
																	productionType,
																	script
																)
															}
															className="mr-2"
														>
															{isLoading
																? "Saving..."
																: isEditMode
																? "Update Ticket"
																: "Create Ticket"}
														</Button>
													</div>
												)}
											</Form>
											<div>
												<TicketAction {...ticketActionProps} />
											</div>
											<TicketExampleModal
												show={showExampleModal}
												onHide={handleCloseExampleModal}
												productionType={exampleType}
											/>
										</Card.Body>
									</Card>
								)}
							</>
						) : (
							//ISEDITMODE END
							<Card className="flex flex-grow-1 flex-col p-4 h-full">
								<Card.Body className="flex flex-col h-full">
									<div className="flex space-x-2 justify-center mb-4">
										{steps.map((step, index) => (
											<div
												key={index}
												className={`h-2 w-2 rounded-full ${
													index === currentStep ? "bg-blue-600" : "bg-gray-300"
												}`}
											/>
										))}
									</div>
									<Form onSubmit={handleSubmit}>
										<div className="w-full">
											{renderStep(steps[currentStep])}
										</div>
									</Form>
									<div className="flex justify-between mt-4">
										{currentStep > 0 && (
											<Button
												variant="secondary"
												onClick={() => setCurrentStep(currentStep - 1)}
											>
												Back
											</Button>
										)}

										{currentStep < steps.length - 1 ? (
											<Button
												variant="primary"
												onClick={() => setCurrentStep(currentStep + 1)}
												disabled={shouldDisableNext(currentStep)}
											>
												Next
											</Button>
										) : (
											<Button
												variant="primary"
												disabled={isLoading}
												onClick={handleSubmit}
											>
												{isLoading ? "Saving..." : "Submit Custom Video!"}
											</Button>
										)}
									</div>
								</Card.Body>
							</Card>
						)}
					</>
				)}
			</div>
		</div>
	);
}
